import papa from 'papaparse';
import { downloadCSV } from '../NoCatalogueProductSelection/utils/orderToCSV';

// Common headers for order CSV exports
const ORDER_CSV_HEADERS = [
  'Order Date',
  'Ship to Name',
  'Ship to Email',
  'Ship to Phone',
  'Ship to Address 1',
  'Ship to Address 2',
  'City',
  'State',
  'Postal Code',
  'Country',
  'Product Name',
  'Variant Name',
  'Product SKU',
  'Quantity',
  'Price',
];

// Helper function to create CSV row data for OrderRequestReview (single orders)
const createOrderRequestCSVRow = (
  order: any,
  lineItem: any,
  shippingAddress?: any,
  memberName?: string,
  variantId?: string
) => ({
  'Order Date': order.createdDate || new Date().toISOString(),
  'Ship to Name': shippingAddress ? `${shippingAddress.first_name || ''} ${shippingAddress.last_name || ''}`.trim() : memberName,
  'Ship to Email': order.creatorOrderRequest?.email || '',
  'Ship to Phone': shippingAddress?.phone || '',
  'Ship to Address 1': shippingAddress?.address1 || '',
  'Ship to Address 2': shippingAddress?.address2 || '',
  'City': shippingAddress?.city || '',
  'State': shippingAddress?.province || '',
  'Postal Code': shippingAddress?.zip || '',
  'Country': shippingAddress?.country || '',
  'Product Name': lineItem.productTitle,
  'Variant Name': lineItem.variantTitle,
  'Product SKU': lineItem.sku || variantId,
  'Quantity': lineItem.quantity.toString(),
  'Price': lineItem.price,
});

// Helper function to create CSV row data for bulk orders (PFAApprovalDrawer)
const createBulkOrderCSVRow = (
  order: any,
  lineItem: any,
  variantInfo?: any,
  shippingAddress?: any
) => ({
  'Order Date': order.createdDate || new Date().toISOString(),
  'Ship to Name': shippingAddress ? `${shippingAddress.first_name || ''} ${shippingAddress.last_name || ''}`.trim() : 'N/A',
  'Ship to Email': order.creatorOrderRequest?.email || 'N/A',
  'Ship to Phone': shippingAddress?.phone || 'N/A',
  'Ship to Address 1': shippingAddress?.address1 || 'N/A',
  'Ship to Address 2': shippingAddress?.address2 || 'N/A',
  'City': shippingAddress?.city || 'N/A',
  'State': shippingAddress?.province || 'N/A',
  'Postal Code': shippingAddress?.zip || 'N/A',
  'Country': shippingAddress?.country || 'N/A',
  'Product Name': lineItem.title || variantInfo?.product_title || 'N/A',
  'Variant Name': lineItem.variant_title || variantInfo?.title || 'N/A',
  'Product SKU': lineItem.sku || variantInfo?.sku || lineItem.variant_id,
  'Quantity': lineItem.quantity?.toString() || '1',
  'Price': lineItem.price?.toString() || variantInfo?.price || '0',
});

// Generate CSV for single order request (used in OrderRequestReview)
export const generateOrderRequestCSV = async (
  order: any,
  currentRequest: Record<string, any>,
  member: { name: string }
): Promise<void> => {
  const rows = [];
  const shippingAddress = order.creatorOrderRequest?.shipping_address;

  for (const [variantId, item] of Object.entries(currentRequest)) {
    if (!item.removed) {
      rows.push(createOrderRequestCSVRow(order, item, shippingAddress, member.name, variantId));
    }
  }

  if (rows.length === 0) {
    throw new Error('No valid items found for CSV export');
  }

  const csv = papa.unparse({
    fields: ORDER_CSV_HEADERS,
    data: rows,
  });

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const fileName = `Order_Request_${member.name.replace(/\s+/g, '_')}_${today}`;

  await downloadCSV(csv, fileName);
};

// Generate CSV for bulk orders (used in PFAApprovalDrawer)
export const generateBulkOrderCSV = async (
  orderDetails: any[],
  deletedVariantIds: Set<number> = new Set(),
  localVariantsData: any[] = []
): Promise<void> => {
  const rows = [];

  // Process all active order details
  orderDetails.forEach((orderDetail) => {
    orderDetail.orders?.forEach((order) => {
      if (order.orderRequestStatus === 'PENDING') {
        const shippingAddress = order.creatorOrderRequest?.shipping_address;

        // Filter out deleted variants
        const filteredLineItems = order.creatorOrderRequest?.line_items?.filter((lineItem) =>
          !deletedVariantIds.has(parseInt(lineItem.variant_id, 10))
        ) || [];

        filteredLineItems.forEach((lineItem) => {
          // Find variant info from localVariantsData
          const variantInfo = localVariantsData?.find((v) => v.id === parseInt(lineItem.variant_id, 10));

          rows.push(createBulkOrderCSVRow(order, lineItem, variantInfo, shippingAddress));
        });
      }
    });
  });

  if (rows.length === 0) {
    throw new Error('No valid orders found for CSV export');
  }

  const csv = papa.unparse({
    fields: ORDER_CSV_HEADERS,
    data: rows,
  });

  const todayFormatted = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const fileName = `Bulk_Order_Approval_${todayFormatted}`;

  await downloadCSV(csv, fileName);
};
